/**
 * [INPUT]: 依赖 dashboardService、zinnercms Card/Select/charts、ChartReveal/AnimatedMeter
 * [OUTPUT]: 对外提供 Dashboard（六 tile + 增长 Area + 聊天 Bar + SMS Funnel + status Pie）
 * [POS]: pages 控制台首页
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */

import { useEffect, useMemo, useState } from 'react'
import { TrendingUp, Users, Heart, MessageCircle, CalendarDays, ShieldCheck, ExternalLink } from 'lucide-react'
import { dashboardService } from '@/services/supabase/dashboard'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/zinnercms-design-system/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/zinnercms-design-system/ui/select'
import { Button } from '@/zinnercms-design-system/ui/button'
import { Area, AreaChart, Bar, BarChart, Grid, Legend, Tooltip, XAxis, YAxis } from '@/zinnercms-design-system/charts/cartesian'
import { FunnelChart } from '@/zinnercms-design-system/charts/funnel'
import { Pie, PieChart, Legend as PolarLegend, Tooltip as PolarTooltip } from '@/zinnercms-design-system/charts/polar'
import { AnimatedMeter, ChartReveal } from '@/zinnercms-design-system/motion'
import { Skeleton } from '@/zinnercms-design-system/ui/skeleton'

function StatTile({ title, value, hint, icon: Icon }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className="size-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold tabular-nums tracking-tight">{value}</div>
        {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      </CardContent>
    </Card>
  )
}

const GROWTH_CONFIG = {
  users: { label: '新用户', color: 'pink' },
  prospects: { label: '新对象', color: 'blue' },
  dateLogs: { label: '新往来', color: 'green' },
}

const CHAT_CONFIG = {
  messages: { label: '消息', color: 'pink' },
  chatUsers: { label: '聊天 DAU', color: 'purple' },
}

export default function Dashboard() {
  const [days, setDays] = useState('30')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    dashboardService
      .fetchOverview({ days: Number(days) })
      .then((res) => {
        if (!cancelled) setData(res.data)
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || '加载失败')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [days])

  const pieData = useMemo(() => {
    const pie = data?.statusPie || {}
    return Object.entries(pie).map(([status, value]) => ({ status, value }))
  }, [data])

  const pieConfig = useMemo(
    () => ({
      Exploring: { label: 'Exploring', color: 'blue' },
      Dating: { label: 'Dating', color: 'pink' },
      Steady: { label: 'Steady', color: 'green' },
      Archived: { label: 'Archived', color: 'grey' },
    }),
    [],
  )

  if (loading && !data) {
    return (
      <div className="space-y-4 p-1">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-80 w-full rounded-xl" />
      </div>
    )
  }

  if (error && !data) {
    return <div className="p-4 text-sm text-destructive">{error}</div>
  }

  const tiles = data.tiles
  const fmtPct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`

  return (
    <div className="space-y-5 p-1">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">控制台</h1>
          <p className="text-sm text-muted-foreground">Zinner 增长 · 活跃 · 漏斗（北京时间日切）</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">近 7 天</SelectItem>
            <SelectItem value="30">近 30 天</SelectItem>
            <SelectItem value="90">近 90 天</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <StatTile title="总用户" value={tiles.totalUsers.toLocaleString()} icon={Users} hint="profiles" />
        <StatTile title="今日新增" value={tiles.todayUsers.toLocaleString()} icon={TrendingUp} hint="北京时间今日" />
        <StatTile title="对象总数" value={tiles.totalProspects.toLocaleString()} icon={Heart} />
        <StatTile title="聊天 DAU" value={tiles.chatDauToday.toLocaleString()} icon={MessageCircle} hint="当日发消息用户" />
        <StatTile title="往来总数" value={tiles.totalDateLogs.toLocaleString()} icon={CalendarDays} />
        <StatTile title="验证码成功率" value={fmtPct(tiles.smsSuccessRate)} icon={ShieldCheck} hint="sms verified / sent" />
      </div>

      <ChartReveal className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>增长趋势</CardTitle>
            <CardDescription>新用户 / 新对象 / 新往来 · pink 主系列</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <AreaChart
                data={data.growthSeries}
                config={GROWTH_CONFIG}
                margins={{ top: 20, right: 14, bottom: 28, left: 40 }}
                ariaLabel="增长趋势面积图"
              >
                <Grid />
                <XAxis dataKey="day" maxTicks={8} />
                <YAxis />
                <Area dataKey="users" color="pink" variant="dotted" />
                <Area dataKey="prospects" color="blue" variant="dotted" />
                <Area dataKey="dateLogs" color="green" variant="dotted" />
                <Legend />
                <Tooltip />
              </AreaChart>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>聊天活跃</CardTitle>
            <CardDescription>cms_chat_daily_stats · 消息柱 + DAU</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <BarChart
                data={data.chatSeries}
                config={CHAT_CONFIG}
                margins={{ top: 20, right: 14, bottom: 28, left: 40 }}
                ariaLabel="聊天活跃柱状图"
              >
                <Grid />
                <XAxis dataKey="day" maxTicks={8} />
                <YAxis />
                <Bar dataKey="messages" color="pink" variant="dotted" />
                <Bar dataKey="chatUsers" color="purple" variant="dotted" />
                <Legend />
                <Tooltip />
              </BarChart>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>登录漏斗</CardTitle>
            <CardDescription>短信发送 → 验证成功</CardDescription>
          </CardHeader>
          <CardContent>
            <AnimatedMeter>
              <FunnelChart
                data={data.smsFunnel}
                valueLabel="次数"
                totalLabel="转化"
                color="pink"
                variant="hatched"
                height={260}
                rateMode="total"
                ariaLabel="短信验证漏斗"
              />
            </AnimatedMeter>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>对象状态分布</CardTitle>
            <CardDescription>Exploring / Dating / Steady / Archived</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[260px] w-full">
              <PieChart
                data={pieData}
                config={pieConfig}
                dataKey="value"
                nameKey="status"
                innerRadius={0.55}
                margins={{ top: 20, right: 10, bottom: 10, left: 10 }}
                ariaLabel="对象状态饼图"
              >
                <Pie variant="dotted" />
                <PolarLegend isClickable align="center" />
                <PolarTooltip />
              </PieChart>
            </div>
          </CardContent>
        </Card>
      </ChartReveal>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>收入（RevenueCat）</CardTitle>
            <CardDescription>MVP 外链；CORS 限制下不在浏览器直连</CardDescription>
          </div>
          <Button asChild variant="outline" size="sm">
            <a href="https://app.revenuecat.com" target="_blank" rel="noreferrer">
              Overview <ExternalLink className="size-3.5" />
            </a>
          </Button>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          项目 projc34a9a9d · Phase 5 经 Chat0IM-api admin 代理后再接趋势图
        </CardContent>
      </Card>
    </div>
  )
}
